let sink = null;
/**
 * Register a destination for PERF lines (in addition to the devtools console).
 * The webview wires this to a postMessage that the extension writes into its
 * "3D Visualizer" Output channel. Left unset in the standalone website.
 */
export function setPerfSink(fn) {
    sink = fn;
}
/** Emit a PERF line to the console and the registered sink (if any). */
export function perfLog(line) {
    console.log(line);
    if (sink) {
        try {
            sink(line);
        }
        catch {
            /* never let logging break the load path */
        }
    }
}
const isEpoch = (v) => typeof v === 'number' && isFinite(v) && v > 0;
function formatExtra(key, value) {
    switch (key) {
        case 'verts':
            return `${value} pts`;
        case 'MB':
            return `${value} MB`;
        case 'px':
            return `${value} px`;
        case 'mode':
        case 'file':
            return `${value}`;
        default:
            return `${key}=${value}`;
    }
}
export class PerfTimer {
    /**
     * @param loadStartedAt wall-clock epoch (Date.now) when the extension began the
     *   load — drives `read+parse` and the honest `total`. Omitted on the website.
     * @param postedAt wall-clock epoch (Date.now) stamped just before the extension
     *   posted the data — drives `transfer`.
     */
    constructor(kind, loadStartedAt, postedAt, receivedAt) {
        this.marks = [];
        this.extra = {};
        this.kind = kind;
        this.t0 = performance.now();
        // `receivedAt` can be supplied for paths where the timer is built after some
        // webview work already happened (e.g. binary PLY constructs it after the
        // fetch, but passes the URI-receipt epoch so `transfer` excludes the fetch).
        this.receivedAt = isEpoch(receivedAt) ? receivedAt : Date.now();
        this.loadStartedAt = isEpoch(loadStartedAt) ? loadStartedAt : undefined;
        this.postedAt = isEpoch(postedAt) ? postedAt : undefined;
        this.last = this.t0;
    }
    /** Record the elapsed time since the previous mark as a named webview phase. */
    mark(phase) {
        const now = performance.now();
        const dt = now - this.last;
        this.marks.push([phase, +dt.toFixed(1)]);
        this.last = now;
        return dt;
    }
    /** Record an externally-measured phase duration (ms), e.g. a fetch timed elsewhere. */
    add(phase, ms) {
        this.marks.push([phase, +Math.max(0, ms).toFixed(1)]);
        this.last = performance.now();
    }
    /** Attach contextual metadata shown in parentheses at the end. */
    note(key, value) {
        this.extra[key] = value;
    }
    /** Attach the source file name, shown right after the kind tag (basename only). */
    file(name) {
        if (typeof name === 'string' && name.length > 0) {
            this.fileName = name.split(/[/\\]/).pop() || name;
        }
    }
    /** Emit (and return) the consolidated single-line summary. */
    summary() {
        const doneAt = Date.now();
        const phases = [];
        // Cross-process phases from the shared wall clock (sum exactly into total).
        if (this.loadStartedAt != null && this.postedAt != null) {
            phases.push(['read+parse', +Math.max(0, this.postedAt - this.loadStartedAt).toFixed(1)]);
        }
        if (this.postedAt != null) {
            phases.push(['transfer', +Math.max(0, this.receivedAt - this.postedAt).toFixed(1)]);
        }
        // Webview phases (build, or fetch/parse/build) measured with performance.now.
        phases.push(...this.marks);
        // Honest end-to-end: wall-clock when anchored, else the webview span.
        const totalMs = this.loadStartedAt != null
            ? Math.max(0, doneAt - this.loadStartedAt)
            : performance.now() - this.t0;
        const phaseStr = phases.map(([p, ms]) => `${p} ${ms}ms`).join(' · ');
        const extras = Object.entries(this.extra)
            .map(([k, v]) => formatExtra(k, v))
            .join(' · ');
        const tag = this.fileName ? `${this.kind} ${this.fileName}` : this.kind;
        const line = `⏱️ PERF[${tag}] ${phaseStr} | total ${totalMs.toFixed(1)}ms${extras ? `  (${extras})` : ''}`;
        perfLog(line);
        return line;
    }
}
